import { useEffect, useState } from "react";

/**
 * Loads a knowledge-source PDF and returns a Blob URL an <iframe> can render.
 *
 * WHY fetch-then-blob, not a plain `<iframe src="/api/...">`?
 * The app authenticates via a Bearer token stored in localStorage and injected
 * by a global window.fetch monkey-patch (see App.tsx). Browser navigations
 * (iframe src, window.open, anchor clicks) bypass window.fetch entirely — the
 * server receives no Authorization header and responds with 401.
 *
 * Solution: use window.fetch (which gets the token injected) to download the
 * PDF binary, wrap it in a Blob URL, and hand that URL to the iframe. The Blob
 * URL is same-origin and needs no auth header once created — which is also why
 * the same URL can back a Download anchor and an open-in-new-tab link.
 *
 * Passing a null sourceId yields no URL and starts no request, so a caller with
 * nothing selected does not need to branch around the hook.
 */
export function usePdfBlob(
  collectionId: number | null,
  sourceId: number | null,
): { blobUrl: string | null; loadError: string | null } {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const apiPath =
    collectionId != null && sourceId != null
      ? `/api/collections/${collectionId}/knowledge-sources/${sourceId}/file`
      : null;

  useEffect(() => {
    // Clear immediately: without this the previous PDF stays on screen while
    // the next one downloads, which reads as the wrong source being shown.
    setBlobUrl(null);
    setLoadError(null);

    if (!apiPath) return;

    let objectUrl: string | null = null;
    let cancelled = false;

    fetch(apiPath)
      .then(async (res) => {
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          throw new Error(body?.error ?? `Failed to load PDF (HTTP ${res.status})`);
        }
        return res.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        // A source switched during the download must not overwrite the newer
        // one's URL, and its object URL has to be released rather than leaked.
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setBlobUrl(objectUrl);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : "Failed to load PDF");
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [apiPath]);

  return { blobUrl, loadError };
}
